import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';

import { AppConfigService, JwtToken } from '@cudirect/shared-ui-library';
import { StatefulService } from '@app/service/stateful.service';
import { tap } from 'rxjs/operators';
import { UserSessionService } from '@shared/service/user-session.service';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';

/**
 * This class will intercept all Http Responses and add JWTs as needed
 */
@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
  constructor(
    private readonly statefulService: StatefulService,
    private readonly userSessionService: UserSessionService,
    private configService: AppConfigService
  ) {}
  public appNumber;

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let request: HttpRequest<unknown>;
    const flowType = sessionStorage.getItem('BMP.FlowType') ?? 'OLA';

    // TODO: Remove header adding logic. This doesent belong in the interceptor
    if (req.url.includes('cpapi')) {
      // Adding this to avoid failed bundle save on page naviation to logout/login
      if (this.userSessionService.logoutInProgress) {
        return EMPTY;
      }

      // Splitting the method to fix Sonar Gate Cognitive Complexity Issue
      this.setApplicationId(req, flowType);

      // Adds Header for DataDog RUM
      // Probably a better way to do this, but this is a working solution for now
      if (this.appNumber && this.appNumber !== '-1') {
        sessionStorage.setItem('appNumber', JSON.stringify(this.appNumber));
      }

      let tokenStr;
      if (this.configService.settings.arcDXTokenStorageType === 'sessionStorage') {
        tokenStr = sessionStorage.getItem('ecp-user-session');
      } else {
        tokenStr = localStorage.getItem('ecp-user-session');
      }

      const authReq: HttpRequest<unknown> = req;

      // Leaving this here temporarily until we settle on a bundle guid method.
      // if (req.headers.has('Authorization')) {
      //   // const Authorization = JSON.parse(sessionStorage.getItem('cuASSD:ss'))?.authorization;
      //   // if (Authorization && Authorization !== req.headers.get('Authorization')) {
      //   //   console.log('Got one!');
      //   //   console.log('Headers have ' + req.headers.get('Authorization'));
      //   //   console.log('Session has ' + Authorization);
      //   //   console.log(JSON.stringify(req));
      //   // }

      //   // authReq = req.clone({
      //   //   setHeaders: {
      //   //     Authorization
      //   //   }
      //   // });
      // } else {
      //   authReq = req;
      // }
      const hasVersion = authReq?.headers?.has('x-api-version');
      if (tokenStr) {
        const token: JwtToken = JSON.parse(tokenStr);
        const header = {
          [RequestHeaderConst.cacheControlName]: 'no-cache',
          [RequestHeaderConst.pragma]: 'no-cache',
          Usertoken: token.access_token,
          AppNumber: this.appNumber ?? '-1',
          FlowType: flowType,
          [RequestHeaderConst.origin]: location.origin
        };
        if (!hasVersion) {
          header['x-api-version'] = '1.0';
        }
        request = authReq.clone({
          setHeaders: header
        });
      } else {
        const header = {
          [RequestHeaderConst.cacheControlName]: 'no-cache',
          [RequestHeaderConst.pragma]: 'no-cache',
          // TODO: This is only used for cpapi/api calls, and they are all on v1.
          // Should be moved to where the call is made, not added by the interceptor
          AppNumber: this.appNumber ?? '-1',
          FlowType: flowType,
          [RequestHeaderConst.origin]: location.origin
        };
        if (!hasVersion) {
          [RequestHeaderConst.apiVersion] = '1.0';
        }
        request = authReq.clone({
          setHeaders: header
        });
      }
    } else {
      request = req;
    }
    return next.handle(request).pipe(
      tap({
        next: () => {},
        error: (error: HttpErrorResponse) => {
          const errorCode = 401;
          if (error.status === errorCode && !req.url.includes('logout')) {
            // Added this check to prevent bundle calls from triggering logout attempts after a logout.
            if (this.userSessionService.token) {
              this.userSessionService.logOut(true);
            }
          }
        },
        complete: () => {}
      })
    );
  }

  /**
   * Setting up the ApplicationId
   */
  private setApplicationId(req: HttpRequest<unknown>, flowType: string): void {
    // If there's a JWT token in storage, we're going to add it to the request headers
    if (!req.url.includes('appInit')) {
      // When we submit application, bundle will be erased and re-created uploan login call.
      // At this point, Reading applicationid from bundle becomes null.
      // So, Fetching the applicationId from session when we make a call from Postapp.
      const sessionAppId = JSON.parse(sessionStorage.getItem('appNumber'));
      if (+sessionAppId > -1) {
        this.appNumber = sessionAppId;
        return;
      }
      if (flowType.includes('OLA')) {
        this.appNumber = this.statefulService.wrap('ola:loan/loanNumber').valueString;
      } else {
        this.appNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
      }

      this.appNumber = this.appNumber ?? sessionAppId;
    }
  }
}
